import type { ReactNode } from "react";
import { IconCheck, IconShield } from "../../components/icons";
import { Skeleton } from "../../components/ui";
import { useI18n } from "../../i18n";
import type { MessageKey } from "../../i18n/strings";
import { monogram } from "../../lib/format";
import { PageShell } from "../PageShell";

export function MerchantLine({ name, children }: { name: string; children?: ReactNode }) {
  return (
    <div className="merchant">
      <span className="monogram" aria-hidden="true">
        {monogram(name)}
      </span>
      <span className="merchant-name">{name}</span>
      {children ? <span className="merchant-end">{children}</span> : null}
    </div>
  );
}

export function TrustLine({ merchant }: { merchant: string }) {
  const { t } = useI18n();
  return (
    <p className="trust">
      <IconShield size={16} />
      <span>{t("common.secureNote", { merchant })}</span>
    </p>
  );
}

/** Skeleton for a single-card public page. */
export function CardLoading({ width }: { width?: "doc" | "form" }) {
  const { t } = useI18n();
  return (
    <PageShell narrow={!width} width={width}>
      <main className="card-page" aria-busy="true">
        <div className="card panel">
          <Skeleton width={160} height={16} />
          <Skeleton width={240} height={56} radius={12} />
          <Skeleton height={120} radius={14} />
          <span className="sr-only" role="status">
            {t("common.loadingPage")}
          </span>
        </div>
      </main>
    </PageShell>
  );
}

export function StepList({ steps }: { steps: { label: string; state: "done" | "active" | "todo" }[] }) {
  return (
    <ol className="steps">
      {steps.map((s) => (
        <li key={s.label} className={`step step-${s.state}`}>
          <span className="step-mark" aria-hidden="true">
            {s.state === "done" ? <IconCheck size={13} strokeWidth={2.5} /> : null}
          </span>
          <span className="step-label">{s.label}</span>
        </li>
      ))}
    </ol>
  );
}

/** Maps a server error code to a translated message when a key exists, else the generic one. */
export function errorText(
  t: (k: MessageKey, v?: Record<string, string | number>) => string,
  prefix: string,
  code: string,
  message: string,
  vars: Record<string, string | number> = {},
): string {
  const key = `${prefix}.err.${code}` as MessageKey;
  const out = t(key, { ...vars, message });
  return out === key ? t(`${prefix}.err.generic` as MessageKey, { ...vars, message }) : out;
}
